import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { getUserProfile } from "../../redux/slices/userSlice";
import { logout } from "../../redux/slices/authSlice";

import Logo, { BrandMark } from "../common/Brand";
import {
  DashboardGlyph,
  CampaignGlyph,
  BillingGlyph,
  ReportGlyph,
  ProfileGlyph,
} from "../common/icons";
import theme from "../../theme";
import infoIcon from "../../assets/icons/infoIcon.svg";

import {
  LogoContainer,
  Menu,
  SidebarContainer,
  SidebarFooter,
} from "./SidebarStyle";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", Icon: DashboardGlyph, exact: true },
      {
        to: "/reports",
        label: "Report and analytics",
        Icon: ReportGlyph,
        match: ["/view-report/"],
      },
    ],
  },
  {
    label: "Campaigns",
    items: [
      {
        to: "/campaigns",
        label: "My campaigns",
        Icon: CampaignGlyph,
        match: ["/create-campaign", "/edit-campaign/", "/view-campaign/"],
      },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/billing", label: "Billing and payments", Icon: BillingGlyph },
      { to: "/profile", label: "Profile", Icon: ProfileGlyph },
    ],
  },
];

const isItemActive = (item, path) => {
  if (item.exact ? path === item.to : path.startsWith(item.to)) return true;
  return (item.match || []).some((prefix) => path.startsWith(prefix));
};

const Sidebar = ({ forwardRef, collapsed = false, onNavigate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out!",
      icon: null,
      iconHtml: `<img src=${infoIcon} alt="" style="width:50px;">`,
      showCancelButton: true,
      confirmButtonColor: theme.color.brand,
      cancelButtonColor: theme.color.danger,
      confirmButtonText: "Yes, logout!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(logout());
        navigate("/login");
      }
    });
  };

  const name = [user.data?.firstName, user.data?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <SidebarContainer ref={forwardRef} $collapsed={collapsed}>
      <LogoContainer $collapsed={collapsed}>
        <Link to="/" aria-label="ClickCircle home">
          {collapsed ? <BrandMark size={30} /> : <Logo size={30} />}
        </Link>
      </LogoContainer>

      <Menu $collapsed={collapsed}>
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="group-label">{group.label}</p>
            <ul>
              {group.items.map((item) => {
                const active = isItemActive(item, pathname);
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.exact}
                      className={active ? "active" : ""}
                      title={collapsed ? item.label : undefined}
                      onClick={onNavigate}
                    >
                      <item.Icon />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </Menu>

      <SidebarFooter $collapsed={collapsed}>
        <h4>{name || "ClickCircle"}</h4>
        <p>{user.data?.userReference || "Advertiser account"}</p>
        <button type="button" onClick={handleLogout}>
          Log out
        </button>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar;
